import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import type { OnboardingState, OnboardingStep } from '@/hooks/useOnboarding'
import type { InstallMethod } from '@/lib/platform'
import { Button } from './Button'
import { Dialog } from './Dialog'

/** iOS share glyph, so the instruction points at something recognisable. */
function IconShare() {
  return (
    <svg viewBox="0 0 24 24" className="inline size-4 shrink-0 -translate-y-px text-accent" fill="none" aria-hidden="true">
      <path
        d="M12 15V4m0 0L8.5 7.5M12 4l3.5 3.5M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" className="inline size-4 shrink-0 -translate-y-px text-accent" fill="none" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" fill="currentColor" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" />
    </svg>
  )
}

/** A numbered manual instruction — used only where no install API exists. */
function Instruction({ index, children }: { index: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="signage mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-[3px] bg-elevated text-[9px] text-accent">
        {index}
      </span>
      <span className="text-sm text-muted">{children}</span>
    </li>
  )
}

/**
 * Install guidance, which differs per platform in ways that matter.
 *
 * iOS has no install API: Safari can only be talked through Share → Add to
 * Home Screen, and other iOS browsers cannot install at all, so the honest
 * instruction there is "open this in Safari". Chromium fires
 * `beforeinstallprompt`, which gives a real button — but only sometimes, so a
 * manual fallback is always available.
 */
function InstallStep({ method, onDone }: { method: InstallMethod; onDone: () => void }) {
  const install = useInstallPrompt()
  const [busy, setBusy] = useState(false)

  // Advance automatically once the app is installed.
  useEffect(() => {
    if (install.installed) onDone()
  }, [install.installed, onDone])

  if (method === 'ios-safari') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Adding Hermes to your home screen gives it its own icon and is the only way iPhones allow
          notifications.
        </p>
        <ol className="space-y-3">
          <Instruction index={1}>
            Tap <IconShare /> <span className="text-ink">Share</span> in the Safari toolbar.
          </Instruction>
          <Instruction index={2}>
            Scroll down and choose <span className="text-ink">Add to Home Screen</span>.
          </Instruction>
          <Instruction index={3}>
            Open Hermes from your home screen, then come back to turn on notifications.
          </Instruction>
        </ol>
      </div>
    )
  }

  if (method === 'ios-other-browser') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted">
          On iPhone only Safari can add a site to the home screen, and notifications only work from
          there.
        </p>
        <ol className="space-y-3">
          <Instruction index={1}>Open this page in Safari.</Instruction>
          <Instruction index={2}>
            Tap <IconShare /> <span className="text-ink">Share</span>, then{' '}
            <span className="text-ink">Add to Home Screen</span>.
          </Instruction>
        </ol>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Installing Hermes gives it its own window and lets notifications arrive when the tab is
        closed.
      </p>

      {install.available ? (
        <Button
          variant="primary"
          loading={busy}
          onClick={async () => {
            setBusy(true)
            try {
              if (await install.promptInstall()) onDone()
            } finally {
              setBusy(false)
            }
          }}
        >
          Install Hermes
        </Button>
      ) : (
        <ol className="space-y-3">
          <Instruction index={1}>
            Open the browser menu <IconMenu />.
          </Instruction>
          <Instruction index={2}>
            Choose <span className="text-ink">Install app</span> or{' '}
            <span className="text-ink">Add to Home screen</span>.
          </Instruction>
        </ol>
      )}
    </div>
  )
}

/** One destination, with what it is for. */
function Place({ name, url, what }: { name: string; url: string; what: string }) {
  return (
    <li className="board-row py-2.5">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="signage-tight text-sm text-ink underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
      >
        {name}
      </a>
      <p className="mt-0.5 text-sm text-muted">{what}</p>
    </li>
  )
}

/**
 * The first thing a brand-new account sees.
 *
 * Two sites and one app decision is genuinely everything someone needs to start
 * using this, and a person who just chose a password is the most attentive they
 * will ever be. Anything longer belongs on the guides page, which this links to
 * rather than duplicating.
 */
function WelcomeStep({ state }: { state: OnboardingState }) {
  const { server, user } = useAuth()
  const pending = user?.approvalState === 'pending'

  return (
    <div className="space-y-4">
      {pending ? (
        <p className="rounded-[3px] border border-warn/30 bg-warn-dim px-3 py-2 text-sm text-warn">
          An admin still needs to approve you before you can watch. Everything below is worth
          setting up while you wait.
        </p>
      ) : null}

      <ul>
        <Place
          name="Watch"
          url={server?.jellyfinUrl ?? 'https://watch.replogle.dev'}
          what="Everything on the server. Sign in with the account you just made."
        />
        {server?.seerrUrl ? (
          <Place
            name="Request"
            url={server.seerrUrl}
            what="Ask for a film or series. It downloads automatically and lands on the server."
          />
        ) : null}
      </ul>

      <div>
        <p className="signage text-[10px] text-muted">On your TV or phone</p>
        <p className="mt-1.5 text-sm text-muted">
          iPhone and iPad: install <span className="text-ink">Streamyfin</span>. Apple TV:{' '}
          <span className="text-ink">Infuse</span> or <span className="text-ink">Swiftfin</span>.
          Android TV and Fire Stick: the official <span className="text-ink">Jellyfin</span> app.
          Add the server as <span className="font-mono text-[11px] text-ink">{server?.jellyfinUrl ?? 'watch.replogle.dev'}</span>.
        </p>
        <Link
          to="/guides"
          onClick={state.finish}
          className="signage mt-2 inline-block text-[10px] text-accent hover:underline"
        >
          Full setup guides
        </Link>
      </div>
    </div>
  )
}

function NotificationsStep({ state }: { state: OnboardingState }) {
  const { push, platform } = state

  if (push.status === 'needs-install' || (platform.mobile && !platform.standalone)) {
    return (
      <p className="text-sm text-muted">
        Add Hermes to your home screen first — notifications can only be turned on from the
        installed app.
      </p>
    )
  }

  if (push.status === 'denied') {
    return (
      <p className="text-sm text-muted">
        Notifications are blocked for this site. Allow them in your browser settings, then turn them
        on from your account page.
      </p>
    )
  }

  if (push.status === 'subscribed') {
    return <p className="text-sm text-ok">Notifications are on for this device.</p>
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Get told when something you asked for starts downloading and when it is ready to watch.
        Nothing else.
      </p>
      <Button variant="primary" loading={push.isBusy} onClick={() => void push.enable()}>
        Turn on notifications
      </Button>
    </div>
  )
}

function DoneStep() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">That is everything. Here is where things live:</p>
      <ul className="space-y-2 text-sm text-muted">
        <li>
          <span className="signage text-[10px] text-ink">Board</span> — what you have asked for and
          how far along it is.
        </li>
        <li>
          <span className="signage text-[10px] text-ink">Requests</span> — the full list, filterable.
        </li>
        <li>
          <span className="signage text-[10px] text-ink">Account</span> — notifications, password
          and your invite link.
        </li>
      </ul>
    </div>
  )
}

const TITLES: Record<OnboardingStep, string> = {
  welcome: 'Welcome',
  install: 'Add to your home screen',
  notifications: 'Turn on notifications',
  done: 'You are set up',
}

/**
 * First-run walkthrough.
 *
 * Shown once, and only when there is something to do — a user who already
 * installed the app and allowed notifications never sees it. Every step is
 * skippable, because being unable to dismiss a modal on your first visit is a
 * worse first impression than missing the tour.
 */
export function Onboarding({ state }: { state: OnboardingState }) {
  const [index, setIndex] = useState(0)
  const step = state.steps[Math.min(index, state.steps.length - 1)] ?? 'done'
  const isLast = index >= state.steps.length - 1

  const next = () => (isLast ? state.finish() : setIndex((value) => value + 1))

  return (
    <Dialog
      open={state.open}
      onClose={state.finish}
      title={TITLES[step]}
      description={
        state.steps.length > 1 ? (
          <span className="font-mono text-[11px] text-faint">
            Step {Math.min(index + 1, state.steps.length)} of {state.steps.length}
          </span>
        ) : undefined
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={state.finish}>
            {isLast ? 'Close' : 'Skip'}
          </Button>
          <Button variant={isLast ? 'primary' : 'secondary'} size="sm" onClick={next}>
            {isLast ? 'Done' : 'Next'}
          </Button>
        </div>
      }
    >
      {step === 'welcome' ? (
        <WelcomeStep state={state} />
      ) : step === 'install' ? (
        <InstallStep method={state.platform.installMethod} onDone={next} />
      ) : step === 'notifications' ? (
        <NotificationsStep state={state} />
      ) : (
        <DoneStep />
      )}
    </Dialog>
  )
}
